import { t } from '@bia-auction/shared-i18n';
import type { VehicleMedia } from '@bia-auction/shared-schema';
import { Button } from '@bia-auction/ui';
import { ChevronLeft, ChevronRight, ImageOff, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { MediaImg, useMediaSrc } from './MediaImg';

/**
 * Vehicle photo gallery with a lightbox (M11, redesign-vehicle.md §7). No external lib — a
 * carousel dependency for one screen is not worth the bundle on a consumer-facing app.
 *
 * Images are `object-contain` in the lightbox and `object-cover` in the strip: a buyer judging
 * paintwork must see the whole frame, while the strip wants an even grid. There is no
 * thumbnail variant to request (ADR 0025), so sizing is CSS only.
 */
export function Gallery({ media, alt }: { media: VehicleMedia[]; alt: string }) {
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  const count = media.length;
  const go = useCallback(
    (delta: number) => setIndex((i) => (count === 0 ? 0 : (i + delta + count) % count)),
    [count],
  );

  // Arrow keys and Escape while the lightbox is open — a gallery that traps a keyboard user
  // with no way out is worse than no lightbox.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(false);
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, go]);

  // Resolved **before** the empty-gallery return, because a hook after it would only run on some
  // renders. A mounted gallery does change between zero and one photo — the vehicle page refetches
  // on every sale-lifecycle WS event, so an operator adding (or deleting) the only photo while a
  // bidder has the page open hands this same instance a different-length array — and a changing
  // hook count is a thrown "Rendered more hooks than during the previous render", i.e. the vehicle
  // page dies rather than degrading. `useMediaSrc` takes an optional path for exactly this.
  const current = count > 0 ? media[Math.min(index, count - 1)] : undefined;
  // One hook for the selected item — the main element and the lightbox show the same URL, and a
  // gallery click is exactly the "loads after the token expired" case (ADR 0027).
  // Spread as a bundle (`{...currentMedia}`) rather than picked apart: it carries `src`, the
  // failure recovery and the re-arm together, so no element here can take two of the three.
  const currentMedia = useMediaSrc(current?.url);

  // Keyed on `current`, not on `count`: it narrows the type for everything below, and the two
  // conditions are the same one.
  if (!current) {
    return (
      <div className="flex aspect-[4/3] w-full items-center justify-center rounded-lg border bg-muted text-muted-foreground">
        <span className="flex items-center gap-2 text-sm">
          <ImageOff aria-hidden className="h-4 w-4" />
          {t('bidder.vehicle.noPhotos')}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative overflow-hidden rounded-lg border bg-muted">
        <button
          type="button"
          className="block w-full"
          onClick={() => setLightbox(true)}
          aria-label={t('bidder.vehicle.openPhoto')}
        >
          {current.kind === 'video' || current.kind === 'damage_video' ? (
            // A `<video>` never fires `load` (its readiness events are `loadeddata` and friends),
            // so its guard stays spent after one recovery — the img path is the one that matters
            // for a long-lived gallery, and a video that needs a second recovery still heals on
            // the next mount.
            <video {...currentMedia} controls className="aspect-[4/3] w-full object-cover" />
          ) : (
            <img {...currentMedia} alt={alt} className="aspect-[4/3] w-full object-cover" />
          )}
        </button>
        {count > 1 && (
          <>
            <GalleryArrow side="left" onClick={() => go(-1)} />
            <GalleryArrow side="right" onClick={() => go(1)} />
            <span className="absolute bottom-2 right-2 rounded bg-navy-800/80 px-2 py-0.5 text-xs text-white tabular-nums">
              {index + 1} / {count}
            </span>
          </>
        )}
      </div>

      {count > 1 && (
        <ul className="flex gap-2 overflow-x-auto pb-1">
          {media.map((m, i) => (
            <li key={m.id} className="shrink-0">
              <button
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`${alt} ${i + 1}`}
                aria-current={i === index}
                className={`block overflow-hidden rounded border-2 ${
                  i === index ? 'border-tx-brand' : 'border-transparent'
                }`}
              >
                <MediaImg
                  path={m.url}
                  alt=""
                  loading="lazy"
                  className="h-16 w-20 object-cover"
                />
              </button>
            </li>
          ))}
        </ul>
      )}

      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/90 p-4"
          onClick={() => setLightbox(false)}
        >
          <img
            {...currentMedia}
            alt={alt}
            className="max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('common.close')}
            className="absolute right-3 top-3 text-white"
            onClick={() => setLightbox(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function GalleryArrow({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={side === 'left' ? t('media.moveLeft') : t('media.moveRight')}
      className={`absolute top-1/2 -translate-y-1/2 rounded-full bg-navy-800/70 p-2 text-white hover:bg-navy-800 ${
        side === 'left' ? 'left-2' : 'right-2'
      }`}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
}
